<template>
  <div class="aw-page legal-page">
    <HeadNav selectedLanguage="en" :page="pageKey" />
    <main>
      <section class="content-section">
        <h1>{{ page.title }}</h1>
        <p v-for="text in introParagraphs" :key="text" v-html="text" />
        <section v-for="section in page.sections" :key="section.heading">
          <h2>{{ section.heading }}</h2>
          <ul v-if="section.list && section.listFirst">
            <li v-for="item in section.list" :key="item">{{ item }}</li>
          </ul>
          <p v-for="text in section.body || []" :key="text" v-html="text" />
          <ul v-if="section.list && !section.listFirst">
            <li v-for="item in section.list" :key="item">{{ item }}</li>
          </ul>
          <p v-for="text in section.afterList || []" :key="'after-' + text" v-html="text" />
        </section>
      </section>
    </main>
  </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/site.scss';
import HeadNav from '@/components/HeadNav.vue';

const pages = {
  about: {
    title: 'About Us',
    intro: [
      'Welcome to <a href="https://androidwaves.org/">AndroidWaves</a>, a website focused on Android apps, APK guides, tutorials, reviews, and useful mobile tips.',
      'Our goal is to provide clear and practical information that helps Android users understand apps, features, installation steps, updates, and common problems.',
      'We regularly update our content when app versions, features, or installation methods change. Our articles are written for informational purposes and aim to make Android-related information easier to understand.',
    ],
    sections: [
      {
        heading: 'What We Cover',
        body: [
          'AndroidWaves covers Android apps and APKs, app features and updates, installation guides, troubleshooting, Android tips, reviews, and comparisons.',
        ],
      },
      {
        heading: 'Third-Party Apps',
        body: [
          'Some apps mentioned on AndroidWaves may be developed or distributed by third parties. AndroidWaves is not the developer of those applications unless clearly stated.',
          'Users should check the developer, permissions, compatibility, and source of an application before installing it.',
        ],
      },
      {
        heading: 'Contact Us',
        body: ['If you have a question, correction, copyright concern, or other issue related to our website, please contact us through our Contact Us page.'],
      },
    ],
  },
  contact: {
    title: 'Contact Us',
    intro: 'Have a question about <a href="https://androidwaves.org/">AndroidWaves</a>? Contact us if you need help with our content or want to report an issue.',
    sections: [
      {
        heading: 'You Can Contact Us About',
        listFirst: true,
        list: [
          'Content corrections',
          'Broken links',
          'App information',
          'Copyright concerns',
          'Privacy questions',
          'General website feedback',
          'Other website-related issues',
        ],
        body: [
          'For copyright or content removal requests, include the relevant page URL and explain the issue clearly.',
          'We review legitimate requests and respond as soon as reasonably possible.',
        ],
      },
      {
        heading: 'Email',
        body: ['<strong>Email:</strong> <a href="mailto:contact@androidwaves.org">contact@androidwaves.org</a>'],
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    intro: 'At AndroidWaves, we respect your privacy. This Privacy Policy explains what information may be collected when you visit our website and how that information may be used.',
    sections: [
      {
        heading: 'Information We Collect',
        body: [
          'AndroidWaves may collect information that you voluntarily provide, such as your name or email address when you contact us.',
          'We may also collect limited technical information automatically, including browser type, device information, IP address, referring pages, and general usage information.',
        ],
      },
      {
        heading: 'How We Use Information',
        body: ['Information may be used to:'],
        list: [
          'Respond to user inquiries',
          'Improve website content',
          'Understand website traffic',
          'Maintain website security',
          'Improve user experience',
        ],
        afterList: ['We do not sell personal information to third parties.'],
      },
      {
        heading: 'Cookies',
        body: [
          'AndroidWaves may use cookies and similar technologies to remember preferences, understand website usage, and improve website performance.',
          'Third-party services used on the website may also use cookies according to their own policies.',
        ],
      },
      {
        heading: 'Third-Party Services',
        body: ['We may use third-party services for analytics, advertising, security, or other website functions. These services may collect information according to their own policies.'],
      },
      {
        heading: 'External Links',
        body: ['Our website may contain links to external websites. AndroidWaves is not responsible for the privacy practices or content of third-party websites.'],
      },
      {
        heading: 'Data Security',
        body: ['We take reasonable measures to protect information collected through the website. However, no online service can guarantee complete security.'],
      },
      {
        heading: 'Policy Updates',
        body: ['This Privacy Policy may be updated from time to time. Any changes will be posted on this page.'],
      },
    ],
  },
  terms: {
    title: 'Terms & Conditions',
    intro: 'By using AndroidWaves, you agree to these Terms & Conditions. If you do not agree with these terms, please stop using the website.',
    sections: [
      {
        heading: 'Website Content',
        body: [
          'AndroidWaves provides Android-related information, guides, reviews, tutorials, and other content for informational purposes.',
          'We try to keep our content accurate and up to date, but app features, versions, requirements, and availability may change.',
        ],
      },
      {
        heading: 'Third-Party Applications',
        body: [
          'Some applications mentioned on AndroidWaves may be developed by third parties. We do not claim ownership of third-party applications, trademarks, logos, or copyrighted materials.',
          'Users are responsible for checking an application\'s source, permissions, compatibility, and applicable terms before installation.',
        ],
      },
      {
        heading: 'External Links',
        body: ['AndroidWaves may link to third-party websites. We do not control those websites and are not responsible for their content, availability, or policies.'],
      },
      {
        heading: 'Acceptable Use',
        body: ['You agree not to use AndroidWaves for unlawful activities or attempt to damage, disrupt, or interfere with the website.'],
      },
      {
        heading: 'Content Changes',
        body: ['We may update, remove, or change website content at any time without prior notice.'],
      },
      {
        heading: 'Disclaimer',
        body: ['Your use of AndroidWaves is at your own discretion. We are not responsible for problems resulting from reliance on website content or third-party applications.'],
      },
    ],
  },
  disclaimer: {
    title: 'Disclaimer',
    intro: 'The information provided on AndroidWaves is for general informational purposes only.',
    sections: [
      {
        heading: 'Third-Party Apps',
        body: [
          'Some apps, APK files, tools, and services mentioned on this website may belong to third-party developers. AndroidWaves does not claim ownership of third-party applications unless specifically stated.',
          'We recommend checking the developer, permissions, compatibility, and source before installing any application.',
        ],
      },
      {
        heading: 'Accuracy',
        body: [
          'We make reasonable efforts to keep our content accurate and updated. However, app versions, features, requirements, download availability, and third-party services may change without notice.',
          'We do not guarantee that all information will always be complete, accurate, or current.',
        ],
      },
      {
        heading: 'External Websites',
        body: ['AndroidWaves may contain links to external websites. We are not responsible for the content, security, availability, or privacy practices of third-party websites.'],
      },
      {
        heading: 'User Responsibility',
        body: ['Users are responsible for deciding whether an application or service is suitable for their device and needs. Always keep important data backed up before installing, updating, or changing an application.'],
      },
      {
        heading: 'No Guarantee',
        body: ['AndroidWaves does not guarantee that using any third-party application will be free from errors, security issues, compatibility problems, or account restrictions.'],
      },
    ],
  },
  dmca: {
    title: 'DMCA Policy',
    intro: 'AndroidWaves respects the intellectual property rights of others and responds to valid copyright complaints.',
    sections: [
      {
        heading: 'Copyright Complaint',
        body: ['If you believe copyrighted material has been published on AndroidWaves without authorization, please contact us with:'],
        list: [
          'Your full name',
          'Your contact information',
          'The URL of the reported material',
          'A description of the copyrighted work',
          'An explanation of the copyright concern',
          'Confirmation that the information provided is accurate',
        ],
      },
      {
        heading: 'Contact',
        body: ['Send copyright complaints to: <strong>Email:</strong> <a href="mailto:contact@androidwaves.org">contact@androidwaves.org</a>'],
      },
      {
        heading: 'Content Review',
        body: [
          'After receiving a valid complaint, we will review the reported material and take appropriate action when necessary.',
          'This may include removing or disabling access to the reported content or requesting additional information.',
        ],
      },
      {
        heading: 'False Claims',
        body: ['Please do not submit false or misleading copyright claims. Complaints without sufficient information may not be processed.'],
      },
    ],
  },
  cookies: {
    title: 'Cookie Policy',
    intro: 'AndroidWaves uses cookies and similar technologies to improve website functionality, understand traffic, and provide a better browsing experience.',
    sections: [
      {
        heading: 'What Are Cookies?',
        body: ['Cookies are small files stored on your device when you visit a website. They can help websites remember preferences and understand how visitors use their pages.'],
      },
      {
        heading: 'How We Use Cookies',
        body: ['Cookies may be used for:'],
        list: [
          'Website functionality',
          'Analytics',
          'Traffic measurement',
          'User preferences',
          'Advertising and performance',
        ],
        afterList: ['Third-party services may also use their own cookies according to their policies.'],
      },
      {
        heading: 'Managing Cookies',
        body: [
          'You can manage or disable cookies through your browser settings. Disabling some cookies may affect certain website functions.',
          'By continuing to use AndroidWaves, you agree to the use of cookies as described in this policy.',
        ],
      },
    ],
  },
};

export default {
  name: 'LegalPage',
  components: { HeadNav },
  computed: {
    pageKey() {
      return this.$route.meta.pageKey || 'about';
    },
    page() {
      return pages[this.pageKey] || pages.about;
    },
    introParagraphs() {
      const intro = this.page.intro;
      if (!intro) return [];
      return Array.isArray(intro) ? intro : [intro];
    },
  },
};
</script>

<style lang="scss" scoped>
.content-section {
  width: min(720px, calc(100% - 80px));
  margin-left: auto;
  margin-right: auto;
  padding: 40px 0 56px;
}

.content-section h2 {
  margin: 22px 0 8px;
  color: #18231f;
  font-family: var(--aw-font-body);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: 0;
  border-left-width: 3px;
  padding-left: 10px;
}

.content-section section:first-of-type h2 {
  margin-top: 18px;
}

@media (max-width: 640px) {
  .content-section {
    width: calc(100% - 48px);
  }
}
</style>
